import type { OrgScorecardBlockedReasonCodeV1 } from './org-scorecard-reason-codes';
export {
  ORG_SCORECARD_BLOCKED_REASON_CODES_V1,
  type OrgScorecardBlockedReasonCodeV1,
} from './org-scorecard-reason-codes';

export const ORG_SCORECARD_SCHEMA_VERSION = 'org_scorecard_v1' as const;
export const ORG_SCORECARD_KIND = 'org_scorecard' as const;
export const ORG_HEALTH_WEIGHTED_SCORE_FORMULA_REF =
  'approved_formula:org_health_weighted_score_v1' as const;
export const ORG_HEALTH_LETTER_GRADE_FORMULA_REF =
  'approved_formula:org_health_letter_grade_v1' as const;
export const ORG_HEALTH_WEIGHTED_SCORE_FORMULA_VERSION =
  'org_health_weighted_score_v1' as const;
export const ORG_HEALTH_LETTER_GRADE_FORMULA_VERSION =
  'org_health_letter_grade_v1' as const;

export type OrgScorecardSchemaVersionV1 =
  typeof ORG_SCORECARD_SCHEMA_VERSION;
export type OrgScorecardKindV1 = typeof ORG_SCORECARD_KIND;
export type OrgScorecardGradeV1 = 'A' | 'B' | 'C' | 'D' | 'F';
export type OrgScorecardConfidenceV1 = 'high' | 'medium' | 'low';

export type OrgScorecardRouteBindingV1 = Readonly<{
  moduleId: 'org-analysis-optimization';
  intentId: 'org_health';
  operationId: 'org_health_diagnostic';
}>;

export type OrgScorecardFormulaVersionsV1 = Readonly<{
  scoreFormulaVersion: typeof ORG_HEALTH_WEIGHTED_SCORE_FORMULA_VERSION;
  gradeFormulaVersion: typeof ORG_HEALTH_LETTER_GRADE_FORMULA_VERSION;
  policyVersion: string;
}>;

export type OrgScorecardGradeBandV1 = Readonly<{
  grade: OrgScorecardGradeV1;
  bandLowerBound: number;
  bandUpperBound: number;
}>;

export type OrgScorecardRiskTypeV1 =
  | 'span'
  | 'bottleneck'
  | 'depth_chain'
  | 'unmanaged_manager'
  | 'data_governance';

export type OrgScorecardRiskBindingV1 = Readonly<{
  riskId: string;
  rank: number;
  riskType: OrgScorecardRiskTypeV1;
  nodeId: string;
  label: string;
  nodeType: string | null;
  reason: string;
  severity: 'high' | 'medium' | 'low';
  score: number | null;
  metricBindings: Readonly<Record<string, number>>;
  evidenceRefIds: readonly string[];
}>;

export type OrgScorecardOpportunityTypeV1 =
  | 'span_normalization'
  | 'decision_rights_backup'
  | 'layer_depth_review'
  | 'data_gathering_action';

export type OrgScorecardOpportunityBindingV1 = Readonly<{
  opportunityId: string;
  rank: number;
  interventionType: OrgScorecardOpportunityTypeV1;
  title: string;
  rationale: string;
  sourceRiskId: string;
  financialImpact: 'unknown' | 'not_quantified_in_slice_1';
  evidenceRefIds: readonly string[];
}>;

export type OrgScorecardEvidenceKeyV1 =
  | 'org_state_as_is'
  | 'span_layer_metrics'
  | 'position_cost_roster'
  | 'function_taxonomy'
  | 'org_baseline_snapshot';

export type OrgScorecardEvidenceBindingV1 = Readonly<{
  evidenceKey: OrgScorecardEvidenceKeyV1;
  evidenceRefId: string;
  source: string;
  asOf: string;
  orgStateId: string;
}>;

export type OrgScorecardAssumptionV1 = Readonly<{
  assumptionCode: string;
  statement: string;
  evidenceRefIds: readonly string[];
}>;

export type OrgScorecardDataGapCodeV1 =
  | 'missing_position_cost_roster'
  | 'missing_function_taxonomy'
  | 'missing_historical_baseline'
  | 'missing_hotspot_labels';

export type OrgScorecardDataGapV1 = Readonly<{
  gapCode: OrgScorecardDataGapCodeV1;
  statement: string;
  unlock: string;
  affectedClaims: readonly string[];
}>;

export type OrgScorecardAnswerV1 = Readonly<{
  kind: OrgScorecardKindV1;
  schemaVersion: OrgScorecardSchemaVersionV1;
  tenantId: string;
  orgStateId: string;
  strategyId?: string;
  asOf: string;
  computedAt: string;
  grade: OrgScorecardGradeV1;
  score: number;
  scoreFormulaRef: typeof ORG_HEALTH_WEIGHTED_SCORE_FORMULA_REF;
  gradeFormulaRef: typeof ORG_HEALTH_LETTER_GRADE_FORMULA_REF;
  formulaVersions: OrgScorecardFormulaVersionsV1;
  routeBinding: OrgScorecardRouteBindingV1;
  summary: Readonly<{
    headline: string;
    executiveTakeaway: string;
    recommendation: string;
    confidence: OrgScorecardConfidenceV1;
  }>;
  componentScores: Readonly<{
    spanRiskScore: number;
    centralityRiskScore: number;
    depthChainRiskScore: number;
  }>;
  topRisks: readonly OrgScorecardRiskBindingV1[];
  topOpportunities: readonly OrgScorecardOpportunityBindingV1[];
  evidence: readonly OrgScorecardEvidenceBindingV1[];
  assumptions: readonly OrgScorecardAssumptionV1[];
  dataGaps: readonly OrgScorecardDataGapV1[];
}>;

export type OrgScorecardBlockedV1 = Readonly<{
  status: 'blocked';
  reasonCode: OrgScorecardBlockedReasonCodeV1;
  message: string;
  missingKeys: readonly string[];
  unlock: string;
}>;

export type OrgScorecardOkV1 = Readonly<{
  status: 'ok';
  answer: OrgScorecardAnswerV1;
}>;

export type OrgScorecardComposeResultV1 =
  | OrgScorecardOkV1
  | OrgScorecardBlockedV1;
